import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import AccountMenu from './AccountMenu';
import { useFeature, useRuntimeConfigLoaded } from '../lib/runtimeConfig';
import { useOpenGlobe } from '../hooks/useOpenGlobe';
import { ExternalLink } from 'lucide-react';
import { viewer3dPath } from '../lib/map-session';

// GeoStack app navigation
const NAV_APPS = [
  { id: 'dashboard', path: '/',         icon: '📊', label: 'Dashboard',    ready: true },
  { id: 'projects',  path: '/projects', icon: '📁', label: 'Projects',     ready: true },
  { id: 'datasets',  path: '/datasets', icon: '📋', label: 'Datasets',     ready: true },
  { id: 'upload',    path: '/upload',   icon: '📤', label: 'Upload',       ready: true },
  { id: 'gis',       path: '/map',      icon: '🗺️', label: 'GIS',          ready: true },
  { id: '3d',        path: '/3d',       icon: '🌐', label: '3D Viewer',    ready: true },
  { id: 'globe',    path: null, action: 'globe', icon: '🌍', label: '3D Globe', ready: true },
  { id: 'earthimager', path: null, url: 'https://erimager-bert.pages.dev/', icon: '📊', label: 'EarthImager (external)', external: true, ready: true },
  { id: 'field',     path: null,        icon: '📡', label: 'Field App',    ready: false },
  { id: 'reports',   path: null,        icon: '📄', label: 'Reports',      ready: false },
  { id: 'help',      path: '/help',     icon: '📖', label: 'Help',         ready: true },
  { id: 'settings',  path: '/settings', icon: '⚙️', label: 'Settings',     ready: true },
];

function Layout({ children, ribbon }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const { theme, toggleTheme, C } = useTheme();
  // The 3D viewer is built on inversion models; hide it when that feature is off.
  const inversionEnabled = useFeature('inversion');
  // GIS-EYE globe (portal app at /globe/): hidden when /api/config reports the feature off.
  const configLoaded = useRuntimeConfigLoaded();
  const globeEnabled = useFeature('globe') && configLoaded;
  const { openGlobe, opening: openingGlobe } = useOpenGlobe();

  // Don't show layout on auth pages
  if (location.pathname === '/signin' || location.pathname === '/signup') {
    return children;
  }

  // For GIS map view and the 3D workbench, render without padding (full-bleed)
  const isMapView = location.pathname === '/map' || location.pathname === '/3d';

  const isActive = (path) => path && location.pathname === path;

  return (
    <div style={{ minHeight: '100vh', background: C.bg0, color: C.text0, fontFamily: 'system-ui,-apple-system,sans-serif', display: 'flex', flexDirection: 'column' }}>
      {/* ── Top bar ── */}
      <div style={{ height: 52, background: C.bg1, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0, position: 'sticky', top: 0, zIndex: 30 }}>
        {/* Sidebar toggle (always visible) */}
        <button
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label="Toggle menu"
          style={{ width: 32, height: 32, borderRadius: 6, background: C.bg3, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}
        >
          ☰
        </button>
        {/* Logo */}
        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#00b8de,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <span style={{ color: '#fff', fontWeight: 900, fontSize: 11 }}>UG</span>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text0 }}>Ultimate-GIS</div>
          <div style={{ fontSize: 9, color: C.cyan, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Geoscience Operating System</div>
        </div>

        <div style={{ flex: 1 }} />

        {/* User area */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Theme toggle */}
          <button onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            style={{ fontSize: 14, padding: '4px 8px', borderRadius: 6, background: C.bg3, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            {theme === 'dark' ? '☀️' : '🌙'}
            <span style={{ fontSize: 10 }}>{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          <AccountMenu />
        </div>
      </div>

      {/* ── Ribbon (full-width, above sidebar+content) ── */}
      {ribbon && ribbon}

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <div style={{ width: collapsed ? 52 : 180, background: C.bg1, borderRight: `1px solid ${C.border}`, flexShrink: 0, transition: 'width .2s', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
            {NAV_APPS.filter(app => (app.id !== '3d' || inversionEnabled) && (app.id !== 'globe' || globeEnabled)).map(app => {
              const active = isActive(app.path);
              return (
                <button
                  key={app.id}
                  onClick={() => {
                    if (app.action === 'globe') { if (!openingGlobe) openGlobe(); }
                    else if (app.url) window.open(app.url, '_blank', 'noopener');
                    // The 3D viewer needs a project (stored models, DEM): reopen the last one.
                    else if (app.id === '3d') navigate(viewer3dPath());
                    else if (app.path) navigate(app.path);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    borderRadius: 8, border: 'none', background: active ? C.bg3 : 'transparent',
                    cursor: (app.path || app.url || app.action) ? 'pointer' : 'default', width: '100%', textAlign: 'left',
                    transition: 'background .15s', opacity: app.ready ? 1 : 0.5,
                  }}
                  onMouseEnter={e => { if (!active && (app.path || app.url)) e.currentTarget.style.background = C.bg2; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 16, flexShrink: 0, width: 20, textAlign: 'center' }}>{app.icon}</span>
                  {!collapsed && (
                    <>
                      <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? C.text0 : C.text2, whiteSpace: 'nowrap' }}>
                        {app.label}
                        {app.external && <ExternalLink size={11} aria-label="opens in a new tab" style={{ marginLeft: 5, verticalAlign: '-1px', opacity: 0.7 }} />}
                      </span>
                      {app.ready
                        ? <span style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: active ? C.cyan : C.text4, flexShrink: 0 }} />
                        : <span style={{ marginLeft: 'auto', fontSize: 9, color: C.text4 }}>soon</span>
                      }
                    </>
                  )}
                </button>
              );
            })}
          </div>
          <button onClick={() => setCollapsed(c => !c)} style={{ padding: '10px', background: 'transparent', border: 'none', borderTop: `1px solid ${C.border}`, color: C.text3, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, overflow: 'auto', background: isMapView ? C.bg0 : undefined }}>
          {isMapView ? children : (
            <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px' }}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Layout;
